import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  CardTitle,
  Row,
  Col,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import TeacherTable from "../../components/Tables/TeacherTable";
import StudentTable from "../../components/Tables/StudentTable";
import axios from "../../services/api/api";
import { GetClassByClassId } from "../../actions/teacherQueries";
import CreateTeacher from "../../components/Modal/CreateTeacherModal";
import AddTeachers from "../../components/Modal/AddTeachersToClass";
import ImportStudentForm from "../../components/Forms/ImportStudentsForm";
import CreateStudent from "../Teacher/CreateStudent";
import { toast } from "react-toastify";
import ConfirmDialog from "../../components/Modal/ConfirmDialog";
import EditStudent from "../../components/Modal/EditStudent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import Pacmanloader from "../../components/Pacmanloader/Pacmanloader";
import { TOAST_CONFIG } from "../../Utilities/toastConfig";
import { useLocation } from "react-router-dom";
import {
  GetStudentsWithLicense,
  GetUserByEmail,
} from "../../actions/adminQueries";
import {
  buildAdminOrderContext,
  findLicenseStatus,
  findProgramLicenseIdForCostCenter,
  hasProgramLicense,
  programLicensesFor,
} from "../../Utilities/licenseHelper";
import { attachClassToStudent } from "../../Utilities/studentHelper";
import StudentLicenseManagemenModal from "../../components/Modal/StudentLicenseManagemenModal";
import useCartStore from "../../stores/useCartStore";
import useDeleteStudent from "../../hooks/useDeleteStudent";
import useRedirectIf from "../../hooks/useRedirectIf";

function ClassOverview(props) {
  const intl = useIntl();
  const location = useLocation();
  const [teacherFilter, setTeacherFilter] = useState("");
  const [studentFilter, setStudentFilter] = useState("");
  const [allTeachers, setAllTeachers] = useState(location.state?.teachers);
  // Vyn får bara schoolId i state, men licensnamnen slås upp mot skolans programlicenser.
  // Skolan skrivs till localStorage av klass- och elevlistan man passerar på vägen hit.
  const [school] = useState(() => JSON.parse(localStorage.getItem("school")));
  const [teacherModal, setTeacherModal] = useState(false);
  const [imporStudentsModal, setImporStudentsModal] = useState(false);
  const [detachTeacherModal, setDetachTeacherModal] = useState(false);
  const [teacherToDetach, setTeacherToDetach] = useState({});
  const [teacherToUpdate, setTeacherToUpdate] = useState(null);
  const [createTeacherModal, setCreateTeacherModal] = useState(false);
  const [studentToUpdate, setStudentToUpdate] = useState(null);
  const [studentEditModal, setStudentEditModal] = useState(false);
  const [createStudentModal, setCreateStudentModal] = useState(false);

  const classId = location.state?.class?.id;
  const classQuery = GetClassByClassId(classId);
  const licenseStatusQuery = GetStudentsWithLicense(
    school?.id ?? location.state?.schoolId,
  );

  const currentUserQuery = GetUserByEmail(localStorage.getItem("email"));
  const clearCart = useCartStore((s) => s.clearCart);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);

  const classProgramLicenseId = () =>
    findProgramLicenseIdForCostCenter(
      school,
      classQuery.data?.programme?.costCenter,
    );

  const licensesFor = (student) =>
    programLicensesFor(
      school,
      findLicenseStatus(licenseStatusQuery.data, student.id),
      classProgramLicenseId(),
    );

  // Kryssruta bara för den som saknar klassens elevlicens — samma regel som lärarens
  // klassadministration använder via requiredLicenseIsMissing. Vakten mot isPending
  // behövs för att alla annars läses som "saknar licens" innan svaret kommit.
  const licenseNeededFor = (student) =>
    !licenseStatusQuery.isPending &&
    !hasProgramLicense(
      findLicenseStatus(licenseStatusQuery.data, student.id),
      classProgramLicenseId(),
    );

  // Alla tre memoiseras: ShopStudentsSelect har preselectedStudents och allStudents
  // som beroenden och skriver om urvalet, så nya arrayer varje render skulle radera
  // admins egna kryss så snart vyn renderar om.
  const studentsWithClass = useMemo(
    () =>
      (classQuery.data?.students || []).map((s) =>
        attachClassToStudent(s, classQuery.data),
      ),
    [classQuery.data],
  );

  const preselectedStudents = useMemo(
    () => studentsWithClass.filter((s) => selectedStudentIds.includes(s.id)),
    [studentsWithClass, selectedStudentIds],
  );

  const orderContext = useMemo(
    () =>
      buildAdminOrderContext(
        school,
        currentUserQuery.data,
        classQuery.data?.programme?.costCenter,
        // Klasslistan, inte hit — den här vyn kräver location.state.
        "/superuser/schools/classes",
      ),
    [school, currentUserQuery.data, classQuery.data],
  );

  const toggleStudentSelection = (studentId) =>
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );

  const studentsNeedingLicense = () =>
    (classQuery.data?.students || []).filter(licenseNeededFor);

  const toggleSelectAllNeedingLicense = () =>
    setSelectedStudentIds((prev) =>
      prev.length === studentsNeedingLicense().length
        ? []
        : studentsNeedingLicense().map((s) => s.id),
    );

  const openLicenseModal = () => {
    clearCart();
    setLicenseModalOpen(true);
  };

  const closeLicenseModal = () => {
    clearCart();
    setSelectedStudentIds([]);
    setLicenseModalOpen(false);
  };

  const reloadClass = () => {
    classQuery.refetch();
  };

  const { promptDeleteStudent, deleteStudentPrompt } =
    useDeleteStudent(reloadClass);

  useRedirectIf("/superuser/schools/classes", !classId);

  if (!classId) {
    return null;
  }

  if (classQuery.isPending) {
    return <Pacmanloader />;
  }

  const toggleStudentEditModal = (student) => {
    setStudentToUpdate(null);
    if (student && student.hasOwnProperty("email")) {
      setStudentToUpdate(student);
    }
    setStudentEditModal(!studentEditModal);
  };

  const getallTeachers = () => {
    axios
      .get(`/GetTeachersBySchoolId?schoolId=${location.state.schoolId}`)
      .then((res) => {
        setAllTeachers(res.data.result);
      });
  };

  const filterTeachers = (event) => {
    setTeacherFilter(event.target.value);
  };

  const toggleModals = (entity) => {
    if (entity === "teacher") {
      setTeacherModal(!teacherModal);
    } else if (entity === "studentImport") {
      setImporStudentsModal(!imporStudentsModal);
    } else if (entity === "createStudent") {
      setCreateStudentModal(!createStudentModal);
    }
  };

  const filterStudents = (event) => {
    setStudentFilter(event.target.value);
  };

  const toggleDetachTeacherModal = (teacher) => {
    setTeacherToDetach({ name: teacher.name, id: teacher.id });
    setDetachTeacherModal(true);
  };

  const toggleCreateTeacherModal = (teacher) => {
    setTeacherToUpdate(null);
    if (teacher) {
      getTeacher(teacher.id)
        .then((classes) => {
          teacher.classes = classes;
          setTeacherToUpdate(teacher);
          setCreateTeacherModal(!createTeacherModal);
          return;
        })
        .catch((err) => {
          setCreateTeacherModal(!createTeacherModal);
        });
    } else {
      setCreateTeacherModal(!createTeacherModal);
    }
  };

  const detachTeacher = () => {
    axios
      .post(
        `teacher/removefromclass?teacherid=${teacherToDetach.id}&classid=${classQuery.data.id}`
      )
      .then(() => {
        toast.success(
          <FormattedMessage id="TheTeacherHasBeenRemovedFromTheClass" />,
          TOAST_CONFIG
        );
        reloadClass();
        resetDetachTeacher();
      })
      .catch((err) => {
        toast.warn(
          intl.formatMessage(
            { id: "CouldNotDeleteTeacherTeacherName" },
            {
              teacher: teacherToDetach.name,
            }
          ),
          TOAST_CONFIG
        );
        resetDetachTeacher();
      });
  };

  const getTeacher = (id) => {
    return new Promise((resolve, reject) => {
      if (id) {
        axios.get(`/Teacher/${id}`).then((res) => {
          resolve(res.data.result.classes);
        });
      } else {
        reject("unable to fetch teacher info.");
      }
    });
  };

  const resetDetachTeacher = () => {
    setDetachTeacherModal(false);
    setTeacherToDetach({});
  };

  return (
    <div>
      <div className="card card-body">
        <h5 className="no-margin">
          {classQuery.data.name}
          {school?.name && (
            <span className="text-muted"> — {school.name}</span>
          )}
        </h5>
      </div>
      <Row>
        <Col sm="6">
          <Card body>
            <CardTitle>
              <FormattedMessage id="Teacher" />
            </CardTitle>
            <Row className="search-row">
              <Col className="col-6">
                <Input
                  className="search-input"
                  placeholder={intl.formatMessage({
                    id: "SearchTeacher",
                  })}
                  onChange={filterTeachers.bind(this)}
                />
              </Col>
              <Col className="offset-1 col-5">
                <Button
                  color="primary"
                  outline
                  className="float-end"
                  onClick={() => {
                    toggleModals("teacher");
                  }}
                >
                  <FontAwesomeIcon icon="plus" />
                </Button>
              </Col>
            </Row>
            <TeacherTable
              updateTeacher={toggleCreateTeacherModal}
              detachTeacher={toggleDetachTeacherModal}
              teachers={classQuery.data?.teachers || []}
              filter={teacherFilter}
              fullAccess={true}
            />
          </Card>
        </Col>
        <Col sm="6">
          <Card body>
            <CardTitle>
              <FormattedMessage id="Students" />
            </CardTitle>
            <Row className="search-row">
              <Col className="col-5">
                <Input
                  className="search-input"
                  placeholder={intl.formatMessage({
                    id: "SearchStudent",
                  })}
                  onChange={filterStudents.bind(this)}
                />
              </Col>
              <Col className="col-7">
                <div className="d-flex justify-content-end gap-2 flex-wrap">
                  <Button
                    color="primary"
                    outline
                    onClick={() => toggleModals("createStudent")}
                  >
                    <FontAwesomeIcon icon="plus" />{" "}
                    <FormattedMessage id="AddStudent" />
                  </Button>
                  <Button
                    color="primary"
                    outline
                    onClick={() => toggleModals("studentImport")}
                  >
                    <FontAwesomeIcon icon="upload" />{" "}
                    <FormattedMessage id="ImportStudents" />
                  </Button>
                </div>
              </Col>
            </Row>
            <StudentTable
              toggleEdit={toggleStudentEditModal}
              onDeleteStudent={promptDeleteStudent}
              students={classQuery.data?.students || []}
              licensesFor={licensesFor}
              licenseNeededFor={licenseNeededFor}
              selectedStudentIds={selectedStudentIds}
              onToggleStudent={toggleStudentSelection}
              selectionHeader={
                <div className="d-flex align-items-center justify-content-center">
                  <Input
                    type="checkbox"
                    className="me-2 mt-0"
                    checked={
                      studentsNeedingLicense().length > 0 &&
                      selectedStudentIds.length ===
                        studentsNeedingLicense().length
                    }
                    disabled={studentsNeedingLicense().length === 0}
                    onChange={toggleSelectAllNeedingLicense}
                  />
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    disabled={selectedStudentIds.length === 0}
                    onClick={openLicenseModal}
                  >
                    <FormattedMessage id="AssignLicense" />
                    {selectedStudentIds.length > 0
                      ? ` (${selectedStudentIds.length})`
                      : ""}
                  </Button>
                </div>
              }
              filter={studentFilter}
            />
          </Card>
        </Col>
      </Row>

      <StudentLicenseManagemenModal
        isOpen={licenseModalOpen}
        toggle={closeLicenseModal}
        preselectedStudents={preselectedStudents}
        className={classQuery.data?.name}
        students={studentsWithClass}
        orderContext={orderContext}
      />

      <AddTeachers
        classId={classQuery.data.id}
        isOpen={teacherModal}
        toggle={() => toggleModals("teacher")}
        teachers={allTeachers}
        existingTeachers={classQuery.data.teachers}
        loadTeachers={reloadClass}
        reloadAllTeachers={getallTeachers}
      />

      <EditStudent
        toggle={toggleStudentEditModal.bind(this)}
        isOpen={studentEditModal}
        student={studentToUpdate}
        updateStudents={reloadClass}
        classes={location.state.schoolClasses}
      />

      <ConfirmDialog
        isOpen={detachTeacherModal}
        title={intl.formatMessage({
          id: "RemoveTeacherFromClass",
        })}
        message={
          <div>
            {intl.formatMessage(
              { id: "AreYouSureYouWantToDeleteTeacherNameFromClassName" },
              {
                teacherName: teacherToDetach.name,
                className: classQuery.data.name,
              }
            )}
          </div>
        }
        toggle={resetDetachTeacher}
        onProceed={detachTeacher}
        danger={true}
      />

      {deleteStudentPrompt}

      <Modal
        size="lg"
        isOpen={createStudentModal}
        toggle={() => toggleModals("createStudent")}
      >
        <ModalHeader toggle={() => toggleModals("createStudent")}>
          <FormattedMessage id="AddNewStudentToClass" />
        </ModalHeader>
        <ModalBody>
          <CreateStudent
            classes={[classQuery.data]}
            studentClassId={classQuery.data.id}
            updateStudents={reloadClass}
            dismissModal={() => toggleModals("createStudent")}
          />
        </ModalBody>
      </Modal>

      <Modal
        size="lg"
        isOpen={imporStudentsModal}
        toggle={() => toggleModals("studentImport")}
      >
        <ModalHeader toggle={() => toggleModals("studentImport")}>
          <FontAwesomeIcon icon="upload" />
          &nbsp; <FormattedMessage id="ImportStudents" />
        </ModalHeader>
        <ModalBody>
          <ImportStudentForm
            preSelectedClassId={classQuery.data.id}
            updateStudents={reloadClass}
            dismissModal={() => toggleModals("studentImport")}
            studentClasses={[classQuery.data]}
          />
        </ModalBody>
      </Modal>

      <CreateTeacher
        teacherToUpdate={teacherToUpdate}
        schoolClasses={location.state.schoolClasses}
        isOpen={createTeacherModal}
        toggle={toggleCreateTeacherModal}
        loadTeachers={reloadClass}
        fullAccess={true}
      />
    </div>
  );
}
export default ClassOverview;
